import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import CircleIcon from '@mui/icons-material/Circle';
import { BaseStyle, Pagination, ReactTable, ShortCodeEnums, CoreCommonMessages } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { usePolling } from '@nhl/react-hooks/lib/usePolling';
import { useScroll } from '@nhl/react-hooks/lib/useScroll';

import { DraftState } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { getMillisecondsUntilTime } from '~/core/utils';
import BackToTopButton from '~/views/components/backToTopButton/BackToTopButton';
import NoContent from '~/views/components/noContent/NoContent';
import PageWrapper from '~/views/components/pageWrapper/PageWrapper';
import DraftMiniTracker from '~/widgets/draftMiniTracker/DraftMiniTracker';

import { useDraftQuery } from './api';
import columns from './columns';
import Filters from './components/filters/Filters';
import { ResolvedValues } from './types';
import { ALL_VALUE, DEFAULT_ROUND, PAGE_INDEX_QUERY, PAGE_SIZE } from './utils';

import { DraftHeader, DraftTableContainer, Heading, LiveIndicator, MiniTrackerWrapper } from './styles';

const DraftTracker: FC = () => {
    const intl = useIntl();
    const params = useParams();
    const navigate = useNavigate();
    const isWebview = useIsWebview();
    const [searchParams] = useSearchParams();

    const headerRef = useRef<HTMLDivElement>(null);
    const pageIndex = searchParams.has(PAGE_INDEX_QUERY) ? Number(searchParams.get(PAGE_INDEX_QUERY)) : 0;

    const [isStickyHeader, setIsStickyHeader] = useState<boolean>(false);

    const onScrollChange = useCallback(() => {
        const top = headerRef.current?.getBoundingClientRect().top || Infinity;
        const padding = 8;
        setIsStickyHeader(BaseStyle.LEAGUE_HEADER_HEIGHT + padding >= top);
    }, []);

    useScroll({
        onScrollChanged: onScrollChange,
    });

    const getRoundParam = (): string => {
        if (params.round) {
            return params.round;
        }
        return params.year ? DEFAULT_ROUND : '';
    };

    const yearFromUrl = params.year || 'now';
    const roundFromUrl = getRoundParam();

    const { data, isInitialLoad, isLoading, refetch: refetchDraftPicks } = useDraftQuery(yearFromUrl, roundFromUrl);

    const [isYearChanging, setIsYearChanging] = useState(isInitialLoad);
    const isDraftActive = ![DraftState.OVER_DAY_1, DraftState.OVER, DraftState.FUTURE].includes(data.state);

    const millisToDraftStart = useMemo<number | undefined>(
        () => getMillisecondsUntilTime(data.broadcastStartTimeUTC),
        [data]
    );

    usePolling(refetchDraftPicks, isDraftActive, millisToDraftStart);

    const pageTitle = intl.formatMessage(
        {
            description: 'Title for the Draft Tracker page, to be displayed on a browser tab etc.',
            defaultMessage: 'NHL Draft Picks | {year} | NHL.com',
        },
        {
            year: data?.draftYear || '',
        }
    );

    const resolvedValues: ResolvedValues = useMemo(
        () => ({
            resolvedYear: params.year ? Number(params.year) : data.draftYear,
            resolvedRound: params.round ? params.round : DEFAULT_ROUND,
            resolvedTeam: params.team ? params.team : ALL_VALUE,
            resolvedLeague: params.league ? params.league : ALL_VALUE,
            resolvedAmateurTeam: params.amateurTeam ? params.amateurTeam : ALL_VALUE,
            resolvedCountry: params.country ? params.country : ALL_VALUE,
            resolvedPosition: params.position ? params.position : ALL_VALUE,
        }),
        [data.draftYear, params]
    );

    const isRoundValueAll = resolvedValues.resolvedRound === ALL_VALUE;
    const isTeamValueAll = resolvedValues.resolvedTeam === ALL_VALUE;
    const isLeagueValueAll = resolvedValues.resolvedLeague === ALL_VALUE;
    const isAmateurValueAll = resolvedValues.resolvedAmateurTeam === ALL_VALUE;
    const isCountryValueAll = resolvedValues.resolvedCountry === ALL_VALUE;
    const isPositionValueAll = resolvedValues.resolvedPosition === ALL_VALUE;

    useEffect(() => {
        if (!isLoading) {
            setIsYearChanging(false);
        }
    }, [isLoading]);

    const filteredData = useMemo(() => {
        return (
            data.picks &&
            data.picks.filter((pick) => {
                const teamRequirement =
                    isTeamValueAll || pick.teamAbbrev?.toLowerCase() === resolvedValues.resolvedTeam;
                const leagueRequirement =
                    isLeagueValueAll || pick.amateurLeague?.toLowerCase() === resolvedValues.resolvedLeague;
                const amateurRequirement =
                    isAmateurValueAll || pick.amateurClubName?.toLowerCase() === resolvedValues.resolvedAmateurTeam;
                const countryRequirement =
                    isCountryValueAll || pick.countryCode?.toLowerCase() === resolvedValues.resolvedCountry;
                const positionRequirement =
                    isPositionValueAll || pick.positionCode?.toLowerCase() === resolvedValues.resolvedPosition;

                return (
                    teamRequirement &&
                    leagueRequirement &&
                    amateurRequirement &&
                    countryRequirement &&
                    positionRequirement
                );
            })
        );
    }, [
        data.picks,
        isAmateurValueAll,
        isCountryValueAll,
        isLeagueValueAll,
        isPositionValueAll,
        isTeamValueAll,
        resolvedValues,
    ]);

    const draftHeader = isRoundValueAll ? (
        <FormattedMessage
            description="Header of a draft table indicating that all rounds are being displayed"
            defaultMessage="All Rounds"
        />
    ) : (
        <FormattedMessage
            description="Header of a draft table indicating the current displayed round"
            defaultMessage="Round {selectedRound}"
            values={{ selectedRound: resolvedValues.resolvedRound }}
        />
    );

    const pageSize = isRoundValueAll ? PAGE_SIZE : filteredData?.length;
    const pageCount = Math.ceil(filteredData?.length / pageSize);

    const draftTable = useMemo(
        () => <ReactTable data={filteredData} columns={columns(intl)} pagination={{ pageIndex, pageSize }} />,
        [filteredData, intl, pageIndex, pageSize]
    );

    const urlBase = `/draft/tracker/${resolvedValues.resolvedYear}/${resolvedValues.resolvedRound}`;
    const urlFilters = `/${resolvedValues.resolvedTeam}/${resolvedValues.resolvedLeague}/${resolvedValues.resolvedAmateurTeam}/${resolvedValues.resolvedCountry}/${resolvedValues.resolvedPosition}`;

    return isInitialLoad ? (
        <LoadingDisplay isLoading />
    ) : (
        <PageWrapper
            defaultPageLayout
            theme={BaseStyle.LightTheme}
            pageTitle={pageTitle}
            pageWrapperHeader={
                isWebview && (
                    <MiniTrackerWrapper>
                        <DraftMiniTracker
                            language={intl.locale as ShortCodeEnums.Language}
                            showFullTrackerLink={false}
                        />
                    </MiniTrackerWrapper>
                )
            }
        >
            <LoadingDisplay isLoading={isYearChanging}>
                {!isWebview && (
                    <DraftMiniTracker language={intl.locale as ShortCodeEnums.Language} showFullTrackerLink={false} />
                )}
                <Heading ref={headerRef}>
                    <h1>
                        <FormattedMessage
                            defaultMessage="NHL Draft Tracker"
                            description="Header for Draft Tracker page"
                        />
                    </h1>
                </Heading>
                <Filters resolvedValues={resolvedValues} data={data} setIsYearChanging={setIsYearChanging} />
                {data.picks.length > 0 ? (
                    <DraftTableContainer>
                        <DraftHeader>
                            <h4 aria-level={2}>{draftHeader}</h4>
                            {isDraftActive && (
                                <LiveIndicator>
                                    <CircleIcon />
                                    <FormattedMessage
                                        {...CoreCommonMessages.gameStateDescriptions.long[
                                            ShortCodeEnums.GameState.live
                                        ]}
                                    />
                                </LiveIndicator>
                            )}
                        </DraftHeader>
                        {draftTable}
                        {isRoundValueAll && filteredData?.length > PAGE_SIZE && (
                            <Pagination
                                initialPageIndex={pageIndex}
                                pageCount={pageCount}
                                handlePageUpdated={(pageIndex: number) => {
                                    navigate(urlBase + urlFilters + `?${PAGE_INDEX_QUERY}=${pageIndex}`);
                                }}
                                indexOverride={pageIndex + 1}
                            />
                        )}
                        {isStickyHeader &&
                            createPortal(
                                <BackToTopButton
                                    onClick={() =>
                                        document.getElementById('root')?.scrollIntoView({ behavior: 'smooth' })
                                    }
                                />,
                                document.body
                            )}
                    </DraftTableContainer>
                ) : (
                    <NoContent
                        primaryText={
                            <FormattedMessage
                                description="Text describing that there are currently no picks for the selected year."
                                defaultMessage="There are no draft picks for this year yet."
                            />
                        }
                        secondaryText={
                            <FormattedMessage
                                description="Text describing that the user can view historical draft data by selecting another year"
                                defaultMessage="Explore historic Drafts going back to {earliestYear} by selecting a different year."
                                values={{ earliestYear: data.draftYears[0] }}
                            />
                        }
                    />
                )}
            </LoadingDisplay>
        </PageWrapper>
    );
};

export { DraftTracker as Component };
